import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../../services/alert.service';
import Datatable from '../../../../components/datatable/datatable-xl.component';
import TruncatedName from '../../../../components/forms/truncated-name.component';
import Icon from '../../../../components/forms/icon.component';

interface Props {
  rows: any[];
  isLoading?: boolean;
  isRoot?: boolean;
  isAdmin?: boolean;
  onDelete?: (id: string | number) => Promise<void>;
  /** false = usuario de solo lectura: se ocultan editar/borrar/menú */
  canWrite?: boolean;
  onEdit?: (row: any) => void;
  onDuplicate?: (row: any) => void;
  onToggleEnabled?: (row: any) => void;
  onPreview?: (row: any) => void;
}

function resolveLocale(language: string): string {
  const lang = (language || '').toLowerCase();
  if (lang.startsWith('zh')) return 'zh-CN';
  if (lang.startsWith('en')) return 'en-US';
  return 'es-MX';
}

function formatDate(iso: string, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function TableCPDOperators({
  rows,
  isLoading = false,
  onDelete,
  canWrite = true,
  onEdit,
  onDuplicate,
  onToggleEnabled,
  onPreview
}: Props) {
  const { i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const handleDeleteClick = async (r: any) => {
    const recordLabel =
      r.operatorId && r.operatorId !== '—'
        ? `"${r.operatorId.toUpperCase()}" (ID: #${r.id})`
        : `con ID: #${r.id}`;

    const isConfirmed = await AlertService.question(
      '¡ Atención !',
      `Estás a punto de eliminar el siguiente registro: ${recordLabel}. ¿Verificaste que es el registro correcto que deseas eliminar?`,
      () => Promise.resolve(true),
      'Continuar'
    );

    if (!isConfirmed) return;

    if (onDelete) {
      await onDelete(r.id);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        name: 'ID Operador',
        sortable: true,
        minWidth: '140px',
        selector: (r: any) => r.operatorId,
        cell: (r: any) => (
          <div className="d-flex flex-column">
            <span className="fw-bolder fs-6 text-dark text-uppercase">{r.operatorId}</span>
            <span className="text-muted fs-8">{r.id}</span>
          </div>
        )
      },
      {
        name: 'Descripción',
        sortable: false,
        minWidth: '240px',
        selector: (r: any) => r.description,
        cell: (r: any) => (
          <TruncatedName
            text={r.description || 'Sin descripción'}
            maxWidth={260}
            className="fs-7 text-gray-700"
          />
        )
      },
      {
        name: 'Estado',
        sortable: true,
        center: true,
        minWidth: '100px',
        selector: (r: any) => r.enabled,
        cell: (r: any) => (
          <span
            className={`badge ${r.enabled ? 'badge-light-success text-success' : 'badge-light-danger text-danger'} fw-bold fs-8`}
          >
            {r.enabled ? 'Activo' : 'Inactivo'}
          </span>
        )
      },
      {
        name: 'Template ID',
        sortable: true,
        minWidth: '130px',
        selector: (r: any) => r.templateId,
        cell: (r: any) => (
          <div className="d-flex flex-column align-items-start gap-1">
            <code className="fs-8 px-2 py-1 bg-light rounded text-gray-600">
              {r.templateId || 'sin template'}
            </code>
            {Object.keys(r.overrides ?? {}).length > 0 && (
              <span className="badge badge-light-warning fs-9 fw-bold">
                {Object.keys(r.overrides).length} override(s)
              </span>
            )}
          </div>
        )
      },
      {
        name: 'Última Actualización',
        sortable: true,
        minWidth: '140px',
        selector: (r: any) => r.updatedAt,
        cell: (r: any) => (
          <div className="d-flex flex-column">
            <span className="fs-7 fw-semibold text-gray-800">
              {formatDate(r.updatedAt, locale)}
            </span>
            <span className="text-muted fs-9">{formatDate(r.createdAt, locale)} (Creado)</span>
          </div>
        )
      },
      {
        name: 'Acciones',
        center: true,
        minWidth: '160px',
        cell: (r: any) => {
          if (!canWrite) {
            return (
              <button
                type="button"
                className="btn btn-icon btn-bg-light btn-color-primary btn-active-light-primary w-30px h-30px rounded-3"
                title="Ver política efectiva"
                onClick={() => onPreview?.(r)}
              >
                <Icon icon="ki-magnifier" size="fs-5" />
              </button>
            );
          }
          const isDropdownOpen = openDropdownId === r.id;

          return (
            <div className="d-flex align-items-center gap-2 position-relative">
              {/* Botón Editar */}
              <button
                type="button"
                className="btn btn-icon btn-bg-light btn-color-primary btn-active-light-primary w-30px h-30px rounded-3"
                title="Editar"
                onClick={() => onEdit?.(r)}
              >
                <Icon icon="ki-pencil" size="fs-5" />
              </button>

              {/* Botón Eliminar / Basurero */}
              <button
                type="button"
                className="btn btn-icon btn-bg-light btn-color-danger btn-active-light-danger w-30px h-30px rounded-3"
                title="Eliminar"
                onClick={() => handleDeleteClick(r)}
              >
                <Icon icon="ki-trash" size="fs-5" />
              </button>

              {/* Botón de 3 Puntos Desplegable */}
              <div className="dropdown">
                <button
                  type="button"
                  className={`btn btn-icon btn-bg-light btn-color-gray-600 btn-active-light-dark w-30px h-30px rounded-3 ${isDropdownOpen ? 'show' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(isDropdownOpen ? null : r.id);
                  }}
                >
                  <Icon icon="ki-dots-horizontal" size="fs-4" />
                </button>

                {/* Lista Desplegable Visual */}
                {isDropdownOpen && (
                  <>
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100"
                      style={{ zIndex: 99, background: 'transparent' }}
                      onClick={() => setOpenDropdownId(null)}
                    />
                    <div
                      className="dropdown-menu menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold w-200px py-3 show"
                      style={{
                        position: 'absolute',
                        inset: 'auto 0px 0px auto',
                        transform: 'translate3d(0px, 35px, 0px)',
                        zIndex: 100,
                        display: 'block'
                      }}
                    >
                      <div className="menu-item px-3">
                        <a
                          href="#preview"
                          className="menu-link px-3 fs-7"
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenDropdownId(null);
                            onPreview?.(r);
                          }}
                        >
                          <Icon icon="ki-magnifier" size="fs-6 me-2" /> Ver política efectiva
                        </a>
                      </div>
                      <div className="menu-item px-3">
                        <a
                          href="#toggle"
                          className={`menu-link px-3 fs-7 ${r.enabled ? 'text-warning' : 'text-success'}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenDropdownId(null);
                            onToggleEnabled?.(r);
                          }}
                        >
                          <Icon
                            icon={r.enabled ? 'ki-cross-circle' : 'ki-check-circle'}
                            size="fs-6 me-2"
                          />
                          {r.enabled ? 'Deshabilitar' : 'Habilitar'}
                        </a>
                      </div>
                      <div className="menu-item px-3">
                        <a
                          href="#duplicate"
                          className="menu-link px-3 fs-7 text-info"
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenDropdownId(null);
                            onDuplicate?.(r);
                          }}
                        >
                          <Icon icon="ki-copy" size="fs-6 me-2" /> Duplicar
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        }
      }
    ];
  }, [locale, openDropdownId, canWrite, onEdit, onDuplicate, onToggleEnabled, onPreview]);

  return <Datatable key="operators-table" columns={columns} data={rows} isLoading={isLoading} />;
}
